/**
 * Alternative Service Reservation App
 * assets/reservation-alt.js
 *
 * Features:
 * - 3 weeks minimum advance booking
 * - Terms validation in Step 1
 * - Driver's license field
 * - $125 + tax pricing
 */

const ReservationAltApp = {
  // State
  data: {
    name: '',
    email: '',
    phone: '',
    license: '',           // Driver's license
    guests: 2,
    menu: '',              // Single menu for all guests
    menuLabel: '',
    spice: '',             // Single spice level for all guests
    spiceLabel: '',
    requests: '',
    date: '',
    timeSlot: '',
    timeDisplay: '',
    holdToken: '',
    reservationId: '',
    paymentIntentId: '',
    clientSecret: ''
  },
  
  selectedSlotElement: null,
  config: window.ReservationAltConfig || {},
  
  // Stripe objects
  stripe: null,
  cardElement: null,

  /**
   * Initialize the app
   */
  init() {
    console.log('ReservationAltApp initializing...');
    console.log('API URL:', this.config.apiUrl);
    
    if (!this.config.apiUrl) {
      console.error('API URL is not configured!');
      this.showMessage('Reservation system is not properly configured. Please contact support.', 'error');
      return;
    }
    
    this.setupDateInput();
    this.bindEvents();
    
    // Initialize Stripe if deposit is enabled
    if (this.config.showDeposit) {
      this.initializeStripe();
    }
    
    console.log('ReservationAltApp initialized successfully');
  },

  /**
   * Initialize Stripe Elements
   */
  initializeStripe() {
    const stripeConfig = this.config.stripe;
    
    if (!stripeConfig || !stripeConfig.publishableKey) {
      console.error('Stripe publishable key is not configured!');
      return;
    }

    try {
      this.stripe = Stripe(stripeConfig.publishableKey);
      
      const elements = this.stripe.elements({
        fonts: [
          {
            cssSrc: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap'
          }
        ]
      });

      const style = {
        base: {
          color: '#333333',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '16px',
          '::placeholder': {
            color: '#aab7c4'
          }
        },
        invalid: {
          color: '#e74c3c',
          iconColor: '#e74c3c'
        }
      };

      this.cardElement = elements.create('card', { 
        style: style,
        hidePostalCode: false
      });

      console.log('Stripe initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Stripe:', error);
      this.showMessage('Payment system initialization failed. Please refresh the page.', 'error');
    }
  },

  /**
   * Mount Stripe card element
   */
  mountStripeElement() {
    if (!this.cardElement) {
      console.warn('Card element not initialized');
      return;
    }

    const cardContainer = document.getElementById('card-element');
    if (!cardContainer) {
      console.warn('Card element container not found');
      return;
    }

    if (cardContainer.children.length > 0) {
      return; // Already mounted
    }

    this.cardElement.mount('#card-element');

    this.cardElement.on('change', (event) => {
      const displayError = document.getElementById('card-errors');
      if (displayError) {
        if (event.error) {
          displayError.textContent = event.error.message;
          displayError.style.display = 'block';
        } else {
          displayError.textContent = '';
          displayError.style.display = 'none';
        }
      }
    });

    console.log('Stripe card element mounted');
  },

  /**
   * Calculate pricing
   */
  calculatePricing() {
    const guests = this.data.guests || 1;
    const pricePerPerson = parseInt(this.config.pricePerPerson) || 12500;
    const taxPercent = parseFloat(this.config.taxPercent) || 6;

    const menuAmount = guests * pricePerPerson;
    const tax = Math.round(menuAmount * (taxPercent / 100));
    const total = menuAmount + tax;

    console.log('Pricing calculated:', { guests, pricePerPerson, menuAmount, tax, total });
    return { guests, pricePerPerson, menuAmount, tax, total };
  },

  /**
   * Calculate deposit amount (total in cents)
   */
  calculateDepositAmount() {
    return this.calculatePricing().total;
  },

  /**
   * Format amount for display
   */
  formatAmount(amountInCents) {
    const currency = this.config.stripe?.currency || 'usd';
    const amount = amountInCents / 100;
    
    return new Intl.NumberFormat(this.config.locale || 'en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount);
  },

  /**
   * Make API call to Google Apps Script
   */
  async apiCall(action, params = {}, method = 'GET', body = null) {
    let url = this.config.apiUrl;
    
    const urlParams = new URLSearchParams({ action, ...params });
    url += '?' + urlParams.toString();
    
    console.log(`API Call: ${method} ${action}`, body);
    
    const fetchOptions = {
      method: method,
      redirect: 'follow',
      headers: {}
    };
    
    if (method === 'POST' && body) {
      fetchOptions.headers['Content-Type'] = 'text/plain;charset=utf-8';
      fetchOptions.body = JSON.stringify(body);
    }
    
    try {
      const response = await fetch(url, fetchOptions);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const text = await response.text();
      console.log('API Response:', text);
      
      try {
        return JSON.parse(text);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('API Call Error:', error);
      throw error;
    }
  },

  /**
   * Setup date input constraints (3 weeks minimum)
   */
  setupDateInput() {
    const dateInput = document.getElementById('reservation-date');
    if (!dateInput) return;

    const now = new Date();
    const advanceDays = this.config.advanceDays || 21;

    const minDate = new Date(now);
    minDate.setDate(minDate.getDate() + advanceDays);
    dateInput.min = this.formatDateForInput(minDate);

    const maxAdvanceDays = this.config.maxAdvanceDays || 90;
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + maxAdvanceDays);
    dateInput.max = this.formatDateForInput(maxDate);

    this.closedDays = [1, 2];

    console.log('Date input configured:', {
      min: dateInput.min,
      max: dateInput.max,
      advanceDays: advanceDays
    });
  },

  /**
   * Bind event listeners
   */
  bindEvents() {
    const dateInput = document.getElementById('reservation-date');
    if (dateInput) {
      dateInput.addEventListener('change', (e) => this.handleDateChange(e));
    }

    // No guest change handler for individual preferences - we use single selection
  },

  /**
   * Handle date change - validate closed days and advance booking window
   */
  handleDateChange(e) {
    const selectedDate = e.target.value;

    if (!selectedDate) return;

    const date = new Date(selectedDate + 'T12:00:00');
    const dayOfWeek = date.getDay();
    const closedDays = this.closedDays || [1, 2];
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    if (closedDays.includes(dayOfWeek)) {
      this.showMessage(
        `We are closed on ${dayNames[dayOfWeek]}s. Please select another date.`,
        'error'
      );
      e.target.value = '';
      this.data.date = '';
      const container = document.getElementById('time-slots-container');
      if (container) {
        container.innerHTML = `<p class="time-slots-placeholder">${this.config.messages?.selectDate || 'Please select a date first'}</p>`;
      }
      const checkBtn = document.getElementById('check-availability-btn');
      if (checkBtn) checkBtn.disabled = true;
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const advanceDays = this.config.advanceDays || 21;
    const minDate = new Date(today);
    minDate.setDate(minDate.getDate() + advanceDays);

    if (date < minDate) {
      this.showMessage(
        `Reservations must be made at least ${advanceDays} days (3 weeks) in advance. Please select a later date.`,
        'error'
      );
      e.target.value = '';
      this.data.date = '';
      const container = document.getElementById('time-slots-container');
      if (container) {
        container.innerHTML = `<p class="time-slots-placeholder">${this.config.messages?.selectDate || 'Please select a date first'}</p>`;
      }
      const checkBtn = document.getElementById('check-availability-btn');
      if (checkBtn) checkBtn.disabled = true;
      return;
    }

    this.hideMessage();
    this.loadAvailableSlots();
  },

  /**
   * Navigate to a step
   */
  goToStep(step) {
    console.log('Going to step:', step);
    
    // Validate before moving forward
    if (step === 2 && !this.validateStep1()) return;
    if (step === 3 && !this.validateStep2()) return;

    // Hide all steps
    document.querySelectorAll('.form-step').forEach(el => {
      el.style.display = 'none';
    });

    // Show target step
    const targetStep = document.querySelector(`[data-form-step="${step}"]`);
    if (targetStep) {
      targetStep.style.display = 'block';
    }

    this.updateStepIndicator(step);

    // Mount Stripe on step 3
    if (step === 3) {
      this.populateSummary();
      if (this.config.showDeposit) {
        setTimeout(() => this.mountStripeElement(), 100);
      }
    }

    this.hideMessage();

    // Scroll to top of form
    const formWrapper = document.querySelector('.reservation-form-wrapper');
    if (formWrapper) {
      formWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  },

  /**
   * Update step indicator
   */
  updateStepIndicator(currentStep) {
    for (let i = 1; i <= 3; i++) {
      const stepEl = document.querySelector(`.step[data-step="${i}"]`);
      const lineEl = document.querySelector(`.step-line[data-line="${i - 1}"]`);
      
      if (stepEl) {
        stepEl.classList.remove('active', 'completed');
        if (i < currentStep) {
          stepEl.classList.add('completed');
        } else if (i === currentStep) {
          stepEl.classList.add('active');
        }
      }
      
      if (lineEl) {
        lineEl.classList.toggle('completed', i < currentStep);
      }
    }
  },

  /**
   * Validate Step 2
   */
  validateStep2() {
    if (!this.data.timeSlot) {
      this.showMessage(this.config.messages?.selectSlot || 'Please select a time slot', 'error');
      return false;
    }
    return true;
  },

  /**
   * Load available slots
   */
  async loadAvailableSlots() {
    const date = document.getElementById('reservation-date').value;
    if (!date) return;

    this.data.date = date;
    
    const container = document.getElementById('time-slots-container');
    const loading = document.getElementById('slots-loading');
    const checkBtn = document.getElementById('check-availability-btn');

    container.innerHTML = '';
    loading.style.display = 'flex';
    checkBtn.disabled = true;
    this.data.timeSlot = '';
    this.selectedSlotElement = null;

    try {
      const data = await this.apiCall('getAvailableSlots', { date: date });
      
      loading.style.display = 'none';

      if (data.closed) {
        container.innerHTML = `
          <p class="time-slots-placeholder closed-message">
            🚫 ${data.message || this.config.messages?.closedDay || 'We are closed on this day.'}
          </p>
        `;
        return;
      }

      if (data.success && data.slots && data.slots.length > 0) {
        this.renderTimeSlots(data.slots, container);
      } else {
        container.innerHTML = `
          <p class="time-slots-placeholder">
            ${data.error || this.config.messages?.noSlots || 'No available slots for this date.'}
          </p>
        `;
      }
    } catch (error) {
      console.error('Error loading slots:', error);
      loading.style.display = 'none';
      container.innerHTML = `
        <p class="time-slots-placeholder error-message">
          ${this.config.messages?.error || 'Error loading slots. Please try again.'}
        </p>
      `;
    }
  },

  /**
   * Render time slots
   */
  renderTimeSlots(slots, container) {
    slots.forEach(slot => {
      const slotEl = document.createElement('div');
      slotEl.className = 'time-slot';
      
      const hasCapacity = slot.remainingCapacity >= this.data.guests;
      
      if (!hasCapacity) {
        slotEl.classList.add('disabled');
        slotEl.innerHTML = `
          <div class="time-slot-time">${slot.displayTime}</div>
          <div class="time-slot-capacity">${this.config.messages?.notEnough || 'Not enough spots'}</div>
        `;
      } else {
        slotEl.innerHTML = `
          <div class="time-slot-time">${slot.displayTime}</div>
          <div class="time-slot-capacity">${slot.remainingCapacity} ${this.config.messages?.spotsLeft || 'spots left'}</div>
        `;
        slotEl.addEventListener('click', () => this.selectSlot(slot, slotEl));
      }
      
      container.appendChild(slotEl);
    });
  },

  /**
   * Select a time slot
   */
  selectSlot(slot, element) {
    if (this.selectedSlotElement) {
      this.selectedSlotElement.classList.remove('selected');
    }

    element.classList.add('selected');
    this.selectedSlotElement = element;
    
    this.data.timeSlot = slot.time;
    this.data.timeDisplay = slot.displayTime;

    document.getElementById('check-availability-btn').disabled = false;
  },

  /**
   * Check availability
   */
  async checkAvailability() {
    if (!this.data.timeSlot) {
      this.showMessage(this.config.messages?.selectSlot || 'Please select a time slot', 'error');
      return;
    }

    const btn = document.getElementById('check-availability-btn');
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Checking...';

    try {
      const data = await this.apiCall('checkAvailability', {}, 'POST', {
        date: this.data.date,
        timeSlot: this.data.timeSlot,
        guestCount: this.data.guests
      });

      if (data.success && data.available) {
        this.data.holdToken = data.holdToken;
        this.goToStep(3);
      } else {
        this.showMessage(data.message || 'Slot is no longer available', 'error');
        this.loadAvailableSlots();
      }
    } catch (error) {
      console.error('Error checking availability:', error);
      this.showMessage(this.config.messages?.error || 'Error checking availability.', 'error');
    }

    btn.disabled = false;
    btn.textContent = originalText;
  },

  /**
   * Populate summary with booking info
   */
  populateSummary() {
    document.getElementById('summary-name').textContent = this.data.name;
    document.getElementById('summary-email').textContent = this.data.email;
    document.getElementById('summary-phone').textContent = this.data.phone;
    document.getElementById('summary-license').textContent = this.maskLicense(this.data.license);
    document.getElementById('summary-date').textContent = this.formatDateDisplay(this.data.date);
    document.getElementById('summary-time').textContent = this.data.timeDisplay;
    document.getElementById('summary-guests').textContent = `${this.data.guests} Guest${this.data.guests > 1 ? 's' : ''}`;

    const menuSummarySection = document.querySelector('.menu-selection-summary');
    if (menuSummarySection) menuSummarySection.style.display = 'none';

    if (this.config.showDeposit) {
      const pricing = this.calculatePricing();

      const paidSeatsLabel = document.getElementById('summary-paid-seats-label');
      if (paidSeatsLabel) {
        const priceDisplay = this.formatAmount(pricing.pricePerPerson);
        paidSeatsLabel.textContent = `${pricing.guests} Guest${pricing.guests > 1 ? 's' : ''} × ${priceDisplay}`;
      }

      const menuAmountEl = document.getElementById('summary-menu-amount');
      const taxEl = document.getElementById('summary-tax');
      const totalEl = document.getElementById('summary-total');
      if (menuAmountEl) menuAmountEl.textContent = this.formatAmount(pricing.menuAmount);
      if (taxEl) taxEl.textContent = this.formatAmount(pricing.tax);
      if (totalEl) totalEl.textContent = this.formatAmount(pricing.total);
    }

    console.log('Summary populated');
  },

  /**
   * Mask license number for display
   */
  maskLicense(license) {
    if (!license || license.length < 4) return license;
    const visible = license.slice(-4);
    const masked = '*'.repeat(license.length - 4);
    return masked + visible;
  },

  /**
   * Confirm reservation - MAIN FLOW
   */
  async confirmReservation() {
    const btn = document.getElementById('confirm-btn');
    const originalText = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = this.config.showDeposit 
      ? (this.config.messages?.processingPayment || '💳 Processing payment...') 
      : 'Processing...';

    try {
      const createData = await this.apiCall('createReservation', {}, 'POST', {
        name: this.data.name,
        phone: this.data.phone,
        email: this.data.email,
        license: this.data.license,
        date: this.data.date,
        timeSlot: this.data.timeSlot,
        guestCount: this.data.guests,
        menu: this.data.menu,
        menuLabel: this.data.menuLabel,
        spice: this.data.spice,
        spiceLabel: this.data.spiceLabel,
        specialRequests: this.data.requests,
        holdToken: this.data.holdToken,
        serviceType: 'alternative'
      });

      console.log('Reservation created:', createData);

      if (!createData.success) {
        throw new Error(createData.error || 'Failed to create reservation');
      }

      this.data.reservationId = createData.reservationId;

      // Handle payment
      if (this.config.showDeposit) {
        await this.processStripePayment();
      } else {
        await this.finalizeReservation();
      }
    } catch (error) {
      console.error('Error confirming reservation:', error);
      this.showMessage(error.message || this.config.messages?.error, 'error');
      btn.disabled = false;
      btn.innerHTML = originalText;
    }
  },

  /**
   * Process Stripe Payment
   */
  async processStripePayment() {
    const btn = document.getElementById('confirm-btn');

    try {
      const depositAmount = this.calculateDepositAmount();

      console.log('Creating PaymentIntent...');
      const intentData = await this.apiCall('createPaymentIntent', {}, 'POST', {
        reservationId: this.data.reservationId,
        amount: depositAmount,
        currency: this.config.stripe?.currency || 'usd',
        customerEmail: this.data.email,
        customerName: this.data.name,
        metadata: {
          reservationId: this.data.reservationId,
          date: this.data.date,
          timeSlot: this.data.timeSlot,
          guestCount: this.data.guests,
          serviceType: 'alternative'
        }
      });

      if (!intentData.success) {
        throw new Error(intentData.error || 'Failed to initialize payment');
      }

      this.data.clientSecret = intentData.clientSecret;
      this.data.paymentIntentId = intentData.paymentIntentId;

      console.log('Confirming payment with Stripe...');
      const { error, paymentIntent } = await this.stripe.confirmCardPayment(
        this.data.clientSecret,
        {
          payment_method: {
            card: this.cardElement,
            billing_details: {
              name: this.data.name,
              email: this.data.email,
              phone: this.data.phone
            }
          }
        }
      );

      if (error) {
        console.error('Stripe error:', error);
        const errorEl = document.getElementById('card-errors');
        if (errorEl) {
          errorEl.textContent = error.message;
          errorEl.style.display = 'block';
        }
        throw new Error(error.message);
      }

      if (paymentIntent.status === 'succeeded') {
        console.log('Payment succeeded:', paymentIntent.id);
        await this.finalizeReservation(paymentIntent.id);
      } else {
        throw new Error('Payment was not completed. Please try again.');
      }
    } catch (error) {
      console.error('Payment error:', error);
      this.showMessage(
        this.config.messages?.paymentFailed || error.message || 'Payment failed. Please try again.',
        'error'
      );
      btn.disabled = false;
      btn.innerHTML = '💳 ' + (this.config.messages?.pay_button || 'Pay & Confirm Reservation');
    }
  },

  /**
   * Finalize reservation after payment
   */
  async finalizeReservation(paymentId = null) {
    try {
      const data = await this.apiCall('confirmPayment', {}, 'POST', {
        reservationId: this.data.reservationId,
        paymentId: paymentId || 'NO_PAYMENT_REQUIRED',
        paymentStatus: paymentId ? 'paid' : 'not_required'
      });

      console.log('Payment confirmed:', data);

      if (data.success) {
        this.showSuccessState();
      } else {
        this.showMessage('Confirmation failed. Please contact support.', 'error');
      }
    } catch (error) {
      console.error('Error confirming payment:', error);
      this.showMessage('Error confirming reservation. Please contact support.', 'error');
    }
  },

  /**
   * Show success state
   */
  showSuccessState() {
    document.querySelectorAll('.form-step').forEach(el => {
      el.style.display = 'none';
    });

    const successStep = document.querySelector('[data-form-step="success"]');
    if (successStep) {
      successStep.style.display = 'block';
      
      document.getElementById('success-reservation-id').textContent = this.data.reservationId;
      document.getElementById('success-date').textContent = this.formatDateDisplay(this.data.date);
      document.getElementById('success-time').textContent = this.data.timeDisplay;
      document.getElementById('success-guests').textContent = `${this.data.guests} Guest${this.data.guests > 1 ? 's' : ''}`;
    }

    this.updateStepIndicator(4);

    const formWrapper = document.querySelector('.reservation-form-wrapper');
    if (formWrapper) {
      formWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  },

  /**
   * Show message
   */
  showMessage(text, type = 'error') {
    const box = document.getElementById('reservation-message');
    if (box) {
      box.textContent = text;
      box.className = `reservation-message ${type}`;
      box.style.display = 'block';
      box.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  },

  /**
   * Hide message
   */
  hideMessage() {
    const box = document.getElementById('reservation-message');
    if (box) {
      box.style.display = 'none';
    }
  },

  /**
   * Validate email
   */
  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  },

  /**
   * Format date for input
   */
  formatDateForInput(date) {
    return date.toISOString().split('T')[0];
  },

  /**
   * Format date for display
   */
  formatDateDisplay(dateStr) {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateStr + 'T12:00:00').toLocaleDateString(this.config.locale || 'en-US', options);
  },

  /**
   * Validate Step 1
   */
  validateStep1() {
    const name = document.getElementById('reservation-name').value.trim();
    const email = document.getElementById('reservation-email').value.trim();
    const phone = document.getElementById('reservation-phone').value.trim();
    const license = document.getElementById('reservation-license').value.trim();
    const guests = document.getElementById('reservation-guests').value;
    const termsCheckbox = document.getElementById('accept-terms');

    // Basic fields validation
    if (!name || !email || !phone || !license || !guests) {
      this.showMessage(this.config.messages?.fillRequired || 'Please fill in all required fields', 'error');
      return false;
    }

    // Email validation
    if (!this.isValidEmail(email)) {
      this.showMessage(this.config.messages?.invalidEmail || 'Please enter a valid email address', 'error');
      return false;
    }

    // License validation (at least 5 characters)
    if (license.length < 5) {
      this.showMessage(this.config.messages?.invalidLicense || "Please enter a valid driver's license number", 'error');
      return false;
    }

    // Terms acceptance validation
    if (termsCheckbox && !termsCheckbox.checked) {
      const agreementSection = document.querySelector('.agreement-section');
      if (agreementSection) agreementSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const finalAgreement = document.querySelector('.final-agreement');
      if (finalAgreement) {
        finalAgreement.classList.add('error-highlight');
        setTimeout(() => finalAgreement.classList.remove('error-highlight'), 3000);
      }
      this.showMessage(
        this.config.messages?.acceptTerms || 'Please read and accept all Terms & Conditions to continue',
        'error'
      );
      return false;
    }

    // Store data
    this.data.name = name;
    this.data.email = email;
    this.data.phone = phone;
    this.data.license = license;
    this.data.guests = parseInt(guests);
    this.data.menu = '';
    this.data.menuLabel = '';
    this.data.spice = '';
    this.data.spiceLabel = '';

    const requestsEl = document.getElementById('reservation-requests');
    if (requestsEl) this.data.requests = requestsEl.value.trim();

    this.data.termsAccepted = true;
    this.data.termsAcceptedAt = new Date().toISOString();

    console.log('Step 1 validated:', this.data);
    return true;
  }
};

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  ReservationAltApp.init();
});